import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { first, isNil } from 'lodash';
import classNames from 'classnames';
// Import Store
import { useAppDispatch } from 'store';
import { changeTemplateValue } from 'store/slices/template';
// Import UI Components
import { Dropdown, ExtendedInput, Icon } from '@geneui/components';
// Import Components
import DynamicVariables from './DynamicVariables';
import TemplateTextInput from './TemplateTextInput';
import TemplateCategoryInput from './TemplateCategoryInput';
import UploadFile from 'components/common/UploadFile';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { BLOCKED_BY_SUBSCRIPTION_CLASSNAME, l, TemplateInputTypes } from 'constants/common';
import { DefaultLanguage } from 'constants/template';
// Import Hooks
import useBind from 'hooks/UseBind';
import useRequest from 'hooks/UseRequest';
import useFieldValidity from 'hooks/UseFieldValidity';
import useInputInsertion from 'hooks/UseInputInsertion';
import useImageURLPlaceholder from 'hooks/UseImageURLPlaceholder';
// Import Services
import { TemplateSymbolsCountFields } from 'services/template';
import { Helpers } from 'services';
import { TemplateHttpService } from 'services/http';

const { calculateTemplateBodyLengthWithRegEx } = Helpers;

const PushTemplateContent = ({
    templateData,
    handleChange,
    templateLangKey,
    dynamicVariables,
    shortenerAndTrackerSettings,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const subjectValidationState = useFieldValidity(
        TemplateInputTypes.TitleOfPushNotification,
        l.Subject,
        templateLangKey,
    );
    const bodyValidationState = useFieldValidity(TemplateInputTypes.Body, l.Body, templateLangKey);
    const imageURLPlaceholderDropdownValidationState = useFieldValidity(
        TemplateInputTypes.ImageURLPlaceholderDropdown,
        l.ImageURLPlaceholderDropdown,
        templateLangKey,
    );

    const { insertionHandler, updateInsertionHandler, insertionOptions, setInsertionOptions } = useInputInsertion();

    const handleSubjectChange = useBind(handleChange, TemplateInputTypes.TitleOfPushNotification);
    const handleCategoryChange = useBind(handleChange, TemplateInputTypes.Category);
    const updateSubjectInsertionHandler = useBind(updateInsertionHandler, TemplateInputTypes.TitleOfPushNotification);
    const updateBodyInsertionHandler = useBind(updateInsertionHandler, TemplateInputTypes.Body);

    const inputRef = useRef<HTMLInputElement>(null);

    const categoryValue = templateData?.[TemplateInputTypes.Category];
    const templateBody = templateData?.[TemplateInputTypes.Body];
    const subjectValue = templateData?.[TemplateInputTypes.TitleOfPushNotification];

    const [pageSymbolsCount, setPageSymbolsCount] = useState(Helpers.getByteLen(templateBody));
    const [bodyValue, setBodyValue] = useState(templateBody);
    const [moveCursorToPosition, setMoveCursorToPosition] = useState<number | null>(null);
    const [isUploadedImageLoading, setIsUploadedImageLoading] = useState(false);
    const [isUploadBtnDisabled, setIsUploadBtnDisabled] = useState(true);
    const [file, setFile] = useState<Nullable<Blob>>(null);

    const { doPostRequest } = useRequest();

    // kept out of the hook: an asset upload must not be aborted when the user switches tabs or leaves the page
    const { addAssetRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- upload must survive unmount
            addAssetRequest: TemplateHttpService.addAsset(),
        }),
        [],
    );

    const {
        handleImageURLPlaceholderDropdownChange,
        imagePreviewURL,
        imageURLPlaceholderDropdownData,
        isBlockedImageURLPlaceholderDropdown,
        isDisabledUploadFileBtn,
        isDisabledImageURLPlaceholderDropdown,
    } = useImageURLPlaceholder({
        templateData,
        templateLangKey,
        templateType: TemplateTypeEnum.PushNotification,
        dynamicVariables,
    });

    const updateDynamicVariableBodyOptions = useCallback(() => {
        setInsertionOptions({
            channel: TemplateTypeEnum.PushNotification,
            inputFilterKey: '',
            hintText: '',
            focusedInputKey: TemplateInputTypes.Body,
        });
    }, [setInsertionOptions]);

    const updateDynamicVariableSubjectOptions = useCallback(() => {
        setInsertionOptions({
            channel: TemplateTypeEnum.PushNotification,
            inputFilterKey: 'IsSubjectItem',
            hintText: '',
            focusedInputKey: TemplateInputTypes.TitleOfPushNotification,
        });
    }, [setInsertionOptions]);

    useEffect(() => {
        const result = calculateTemplateBodyLengthWithRegEx(shortenerAndTrackerSettings, bodyValue);
        setPageSymbolsCount(Helpers.getByteLen(result));
    }, [bodyValue, shortenerAndTrackerSettings]);

    const handleBodyChange = (e) => {
        const value = e.target.value;

        setBodyValue(value);
        handleChange(TemplateInputTypes.Body, value);
    };

    const insertTextAtCursorBody = (dynamicVariable) => {
        const textToInsert = dynamicVariable.FieldName;
        const selectionStart = inputRef.current?.selectionStart;
        const firstPart = bodyValue.slice(0, selectionStart);
        const secondPart = bodyValue.slice(selectionStart, bodyValue.length);
        const insertedText = ` {${textToInsert}} `;
        const resultValue = `${firstPart}${insertedText}${secondPart}`;

        setBodyValue(resultValue);
        setMoveCursorToPosition(firstPart.length + insertedText.length);
        handleChange(TemplateInputTypes.Body, resultValue);
    };

    updateBodyInsertionHandler(insertTextAtCursorBody);

    // The caret is placed after the textarea receives the new value; setting it earlier is lost
    // because writing the controlled value moves the caret to the end.
    useEffect(() => {
        if (!isNil(moveCursorToPosition)) {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(moveCursorToPosition, moveCursorToPosition);
            setMoveCursorToPosition(null);
        }
    }, [moveCursorToPosition]);

    const fileInputChangeHandler = (e) => {
        const fileInput = e.currentTarget;
        setFile(first(fileInput.files));
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.FileName,
                value: !fileInput.value ? null : first<AnyObject>(fileInput.files)?.name,
                templateLangKey,
            }),
        );
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.UploadedImageUrl,
                value: '',
                templateLangKey,
            }),
        );
        setIsUploadBtnDisabled(!fileInput.value);
    };

    const uploadImgHandler = () => {
        if (isNil(file)) return;
        setIsUploadedImageLoading(true);
        const formData = new FormData();
        formData.append('file', file);

        doPostRequest(addAssetRequest.request, {
            requestBody: formData,
            successCallback: (Data) => {
                dispatch(
                    changeTemplateValue({
                        propertyName: TemplateInputTypes.UploadedImageUrl,
                        value: first(Data)?.Url,
                        templateLangKey,
                    }),
                );
                setIsUploadedImageLoading(false);
                setIsUploadBtnDisabled(true);
            },
            errorCallback: () => {
                setIsUploadedImageLoading(false);
                setIsUploadBtnDisabled(false);
            },
        });
    };

    const deleteUploadedImageHandler = () => {
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.UploadedImageUrl,
                value: '',
                templateLangKey,
            }),
        );
        dispatch(
            changeTemplateValue({
                propertyName: TemplateInputTypes.FileName,
                value: '',
                templateLangKey,
            }),
        );
        setIsUploadBtnDisabled(true);
        setFile(null);
    };

    return (
        <>
            <DynamicVariables
                dynamicVariables={dynamicVariables}
                clickHandler={insertionHandler}
                insertionOptions={insertionOptions}
            />
            <div className="crm-create-template-page-c-c-h-inputs-row">
                {templateLangKey === DefaultLanguage && (
                    <TemplateCategoryInput value={categoryValue} onChange={handleCategoryChange} />
                )}
                <TemplateTextInput
                    displayNameKey={l.Subject}
                    value={subjectValue}
                    onChange={handleSubjectChange}
                    onBlur={handleSubjectChange}
                    validationState={subjectValidationState}
                    updateInsertionHandler={updateSubjectInsertionHandler}
                    onFocus={updateDynamicVariableSubjectOptions}
                />
            </div>
            <div className="crm-create-template-page-c-c-content">
                <div className="crm-messaging-row">
                    <div className="crm-messaging-row-head">
                        <p>{t(l.SymbolsCountIn, { pageName: t(l.PushNotification) })}</p>
                        <span>{`(${pageSymbolsCount} ${t(l.Bytes)} / ${
                            TemplateSymbolsCountFields[TemplateTypeEnum.PushNotification]
                        } ${t(l.Bytes)})`}</span>
                    </div>
                    <div className={`crm-messaging-row-content ${bodyValidationState.isValid ? '' : 'error-color'}`}>
                        <ExtendedInput
                            ref={inputRef}
                            type="textarea"
                            value={bodyValue}
                            isValid={bodyValidationState.isValid}
                            placeholder={t(l.TypeYourText)}
                            onChange={handleBodyChange}
                            onFocus={updateDynamicVariableBodyOptions}
                        />
                    </div>
                    <p className="error-text">{bodyValidationState.errorText}</p>
                </div>
                <div className="social-n-details-panel">
                    <div className="social-n-details-panel-content">
                        <div>
                            <div className="social-n-details-panel-choose-file">
                                <BlockedBySubscriptionInfo
                                    isBlocked={isBlockedImageURLPlaceholderDropdown}
                                    position="right"
                                >
                                    <div
                                        className={classNames('crm-image-url-placeholder-dropdown', {
                                            [`${BLOCKED_BY_SUBSCRIPTION_CLASSNAME} not-clickable`]:
                                                isBlockedImageURLPlaceholderDropdown,
                                        })}
                                    >
                                        <Dropdown
                                            clearable={true}
                                            data={imageURLPlaceholderDropdownData}
                                            value={templateData?.UploadedImageUrl}
                                            onChange={handleImageURLPlaceholderDropdownChange}
                                            disabled={isDisabledImageURLPlaceholderDropdown}
                                            placeholder={t(l.ImageURLPlaceholderDropdown)}
                                            isValid={imageURLPlaceholderDropdownValidationState.isValid}
                                            errorText={imageURLPlaceholderDropdownValidationState.errorText}
                                        />
                                    </div>
                                </BlockedBySubscriptionInfo>
                                <p className="social-n-details-panel-choose-file-title"> {t(l.Media)}:</p>
                                <div className="social-n-details-panel-browse">
                                    <UploadFile
                                        value={templateData?.FileName}
                                        onChange={fileInputChangeHandler}
                                        acceptedExtensions=".svg,.png,.jpg"
                                        disabled={isDisabledUploadFileBtn}
                                    />
                                </div>
                                <div>
                                    <ButtonWithLoader
                                        onClick={uploadImgHandler}
                                        disabled={
                                            isUploadBtnDisabled || isUploadedImageLoading || isDisabledUploadFileBtn
                                        }
                                        isLoading={isUploadedImageLoading}
                                    >
                                        {t(l.Upload)}
                                    </ButtonWithLoader>
                                </div>

                                <div>
                                    <div className="social-c-file-upload-details">
                                        <p>{t(l.MediaPreview)}:</p>
                                        <div className="social-c-file-upload-details-img-wrapper">
                                            {imagePreviewURL && <img alt="noImage" src={imagePreviewURL} />}
                                        </div>
                                        <Icon
                                            type="bc-icon-trash"
                                            disabled={!imagePreviewURL}
                                            onClick={deleteUploadedImageHandler}
                                        />
                                    </div>
                                </div>
                            </div>
                            <p>{t(l.ImageUploadRequirements)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

PushTemplateContent.propTypes = {
    templateData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    templateLangKey: PropTypes.string.isRequired,
    dynamicVariables: PropTypes.arrayOf(PropTypes.object),
    shortenerAndTrackerSettings: PropTypes.array,
};

PushTemplateContent.defaultProps = {
    shortenerAndTrackerSettings: [],
};

export default PushTemplateContent;
